import FilmwebLink from 'src/components/filmweblink/FilmwebLink';
import { CmsImageForUrl } from "src/components/images/CmsImageForUrl";
import Image from 'src/components/images/Image';
import { convert } from "src/components/streaming/streamingtitlewithproviders/CommonDataStructure";
import ProviderScroller from 'src/components/streaming/streamingtitlewithproviders/ProviderScroller';
import theme from 'src/components/themes/DefaultTheme';
import { StreamingContent, WatchableContent } from 'src/lib/movieinfo/movieinfotypes';
import styled, { css } from 'src/lib/styles/css';

//#region [Props]
type StreamingTitleWithProvidersProps = {
	data: StreamingContent | WatchableContent;
	isCinemaRelevant?: boolean;
	useOverrideCinemaRelevant?: boolean;
};
//#endregion

// MAYBE: shopPop? Tror dette kun var for debugging


//#region [Component]
export default function StreamingTitleWithProviders({ data, isCinemaRelevant = false, useOverrideCinemaRelevant = false }: StreamingTitleWithProvidersProps) {
	const _data = convert(data);
	const _isCinRel = (useOverrideCinemaRelevant && isCinemaRelevant) || _data.isCinemaRelevant;
	const url = _isCinRel ?
		`/film/${_data.filmwebMovieId}`
		: `/streamingguide/${_data.isSeries ? "serie" : "film"}/${_data.streamingId}`;

	// Always use sanity poster if present with fallback to streaming poster (which field is present is controlled by movieinfo)
	// isCinemaRelevant does not affect the use of images.
	const useSanityImage = !!_data.sanityImagePosterUrl;
	const useStreamingImage = !useSanityImage && (_data.imagesPoster?.length ?? 0) > 0;

	return <SStreamingTitle>
		{/*<SPopDEBUG>{data.popularity}</SPopDEBUG>*/}
		<SPosterContainer>
			<SPosterLink to={url} $isCinemaRelevant={_isCinRel} data-missingtext="Plakat mangler">
				{useSanityImage && <CmsImageForUrl imageUrl={_data.sanityImagePosterUrl!} alt={_data.title} sizes={SIZES} />}
				{useStreamingImage && <Image image={_data.imagesPoster!} alt={_data.title} sizes={SIZES} />}
			</SPosterLink>
		</SPosterContainer>
		<SHeaderContainer>
			<SHeader>
				<SHeaderLink to={url}>
					<STitle>{_data.title}</STitle>
					{_data.year && _data.year.length > 3 && <SYear>{_data.year}</SYear>}
				</SHeaderLink>
			</SHeader>
			<SProviderScroller data={_data} isCinemaRelevant={isCinemaRelevant} useCinemaRelevantProp={useOverrideCinemaRelevant} />
		</SHeaderContainer>

	</SStreamingTitle>;
}
//#endregion

const SIZES = `(min-width: 550px) 365px, 100vw`;


//#region [Styles]
/*
const SPopDEBUG = styled.div`
	background-color: red;
	color: white;
	position: absolute;
	top: 0;
	left: 0;
	padding: 2px;
	z-index: 100;
	font-size: 1rem;
	line-height: 1;
`;
*/

const POSTER_WIDTH_MOBILE = 82;

const SStreamingTitle = styled.article`
	overflow: hidden;
	border-radius: 10px;
	color: var(--textcolor);
	position: relative;

	display: flex;
	padding: 10px 0 10px 10px;
	gap: 10px;
	background: var(--buttoncolorAlpha);

	@media (min-width: 550px) {
		padding: 0;
		gap: 0;
		flex-direction: column;
		background: none;
	}
`;

const SPosterContainer = styled.div`
	position: relative;
	flex: 0 0 ${POSTER_WIDTH_MOBILE}px;
`;

type SPosterLinkProps = {
	$isCinemaRelevant: boolean;
}

const SPosterLink = styled(FilmwebLink) <SPosterLinkProps>`
	flex: 0 0 auto;

	${props => props.$isCinemaRelevant ? css`
		@supports (aspect-ratio: 1 / 1.413333) {
			aspect-ratio: 1 / 1.413333;
		}

		@supports not (aspect-ratio: 1 / 1.413333) {
			height: 0;
			box-sizing: border-box;
			padding-bottom: 141.33%;
		}
	`: ""}

	&:empty {
		background: var(--missing-poster);
		position: relative;
		display: block;

		@supports (aspect-ratio: 1 / 1.413333) {
			aspect-ratio: 1 / 1.413333;
		}

		@supports not (aspect-ratio: 1 / 1.413333) {
			height: 0;
			box-sizing: border-box;
			padding-bottom: 141.33%;
		}

		&::after {
			content: attr(data-missingtext);
			display: block;
			font-size: 1em;
			opacity: 0.8;
			position: absolute;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			color: var(--textcolor);
		}
	}
`;


const SHeaderContainer = styled.div`
	background: var(--buttoncolorAlpha);
	color: var(--textcolor);
	text-align: center;
	flex: 1 1 auto;
	max-width: calc(100% - ${POSTER_WIDTH_MOBILE + 10}px);
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	@media (min-width: 550px) {
		border-radius: 0 0 10px 10px;
		padding: 10px;
		max-width: none;
	}
`;

const SHeader = styled.header`
	margin-bottom: 10px;
	@media (max-width: 549px) {
        max-width: 82%; /*this is so that the title won't go under the watchlist icon*/
    }
`;

const SHeaderLink = styled(FilmwebLink)`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	text-decoration: none;
	color: var(--textcolor);

	@media (min-width: 550px) {
		justify-content: center;
		flex-direction: row;
		gap: 0.8em;
	}

	&:hover {
		text-decoration: underline;
	}
`;

const STitle = styled.h1`
	flex: 1 1 100%;
	max-width: 100%;
	font-size: var(--font-title-xxs_size);
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	@media (min-width: 550px) {
		flex: 0 1 auto;
	}
`;

const SYear = styled.span`
	flex: 1 1 100%;
	font-size: var(--font-title-xxs_size);
	font-weight: normal;
	line-height: var(--h1-line-height);
	white-space: nowrap;

	&::after {
		content: ")";
	}

	&::before {
		content: "(";
	}

	@media (min-width: 550px) {
		flex: 0 1 auto;
	}
`;

const SProviderScroller = styled(ProviderScroller)`
	@media (max-width: 549px) {
		max-width: 99%;
	}
`;
//#endregion